import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type {
  MealCompletePickupPayload,
  MealMonitoringDetailData,
  MealMonitoringDetailPayload,
} from "../../../../api/meal/types/mealMonitoringDetailType";
import { handleThunkError } from "../../../../helpers/HandleThunkError";
import {
  clearCompletePickup,
  clearCompletePickupError,
  clearMealMonitoringDetail,
  clearMealMonitoringDetailError,
  completeMealPickup,
  fetchMealMonitoringDetail,
  resetMealMonitoringDetail,
  selectCompletePickup,
  selectCompletePickupError,
  selectIsCompletingMealPickup,
  selectIsLoadingMealMonitoringDetail,
  selectMealMonitoringDetail,
  selectMealMonitoringDetailError,
  setMealMonitoringDetail,
} from "../../../../redux/features/meal/mealMonitoringDetailSlice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { useMealFormDownload } from "./useMealFormDownload";

export const useMealMonitoringDetail = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const detail = useAppSelector(selectMealMonitoringDetail);

  const completePickup = useAppSelector(selectCompletePickup);

  const isLoadingDetail = useAppSelector(selectIsLoadingMealMonitoringDetail);

  const isCompletingPickup = useAppSelector(selectIsCompletingMealPickup);

  const detailError = useAppSelector(selectMealMonitoringDetailError);

  const completePickupError = useAppSelector(selectCompletePickupError);

  const { downloadForm, isDownloading, downloadError, clearDownloadError } =
    useMealFormDownload();

  const getDetail = useCallback(
    (uuid: string, payload: MealMonitoringDetailPayload = {}) => {
      const request = dispatch(
        fetchMealMonitoringDetail({
          uuid,
          payload,
        }),
      );

      void request.then((result) => {
        if (fetchMealMonitoringDetail.rejected.match(result)) {
          handleThunkError(result, {
            navigate,
            showToast: false,
          });
        }
      });

      return request;
    },
    [dispatch, navigate],
  );

  const completePickupMeal = useCallback(
    async (payload: MealCompletePickupPayload, signal?: AbortSignal) => {
      const result = await dispatch(
        completeMealPickup(payload, {
          signal,
        }),
      );

      if (completeMealPickup.fulfilled.match(result)) {
        return result.payload;
      }

      handleThunkError(result, {
        navigate,
        showToast: false,
      });

      throw result.payload ?? new Error("Gagal menyelesaikan pengambilan meal");
    },
    [dispatch, navigate],
  );

  const updateDetail = useCallback(
    (nextDetail: MealMonitoringDetailData) => {
      dispatch(setMealMonitoringDetail(nextDetail));
    },
    [dispatch],
  );

  const clearDetail = useCallback(() => {
    dispatch(clearMealMonitoringDetail());
  }, [dispatch]);

  const clearPickup = useCallback(() => {
    dispatch(clearCompletePickup());
  }, [dispatch]);

  const clearDetailError = useCallback(() => {
    dispatch(clearMealMonitoringDetailError());
  }, [dispatch]);

  const clearPickupError = useCallback(() => {
    dispatch(clearCompletePickupError());
  }, [dispatch]);

  const reset = useCallback(() => {
    dispatch(resetMealMonitoringDetail());
  }, [dispatch]);

  return {
    detail,
    isLoadingDetail,
    detailError,
    getDetail,
    updateDetail,
    clearDetail,
    clearDetailError,
    completePickup,
    isCompletingPickup,
    completePickupError,
    completePickupMeal,
    clearPickup,
    clearPickupError,
    reset,
    downloadForm,
    isDownloading,
    downloadError,
    clearDownloadError,
  };
};

export default useMealMonitoringDetail;
